import { useEffect, useRef, useState } from 'react';

/**
 * `true` des que l'element est entre dans le viewport, et le reste ensuite.
 *
 * Sert a differer le montage d'un media lourd sans dependre de
 * `loading="lazy"` : celui-ci s'appuie sur un IntersectionObserver interne,
 * que Chrome fige sur ce site des qu'un defilement fluide croise le
 * scroll-snap (meme cause que le repli sur `scroll` de `useReveal`). Une
 * capture montee sous ce regime n'est jamais telechargee.
 */
export function useInView<T extends HTMLElement>(
  margin = 200,
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el === null || inView) return undefined;

    function check(): boolean {
      if (el === null) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight + margin && rect.bottom > -margin;
    }

    if (check()) {
      setInView(true);
      return undefined;
    }

    function onScroll(): void {
      if (check()) {
        setInView(true);
        cleanup();
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting === true) {
          setInView(true);
          cleanup();
        }
      },
      { rootMargin: `${String(margin)}px` },
    );

    function cleanup(): void {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    observer.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return cleanup;
  }, [inView, margin]);

  return [ref, inView];
}
